<template>
  <div class="cp-root" :class="label ? 'mt-4' : ''" ref="root">
    <span v-if="label" class="cp-label">{{ label }}</span>
    <div
      class="cp-trigger"
      :class="{ 'cp-trigger--open': open, 'cp-trigger--disabled': disabled }"
      @click="toggle"
    >
      <span class="cp-swatch" :style="value ? { background: value } : {}">
        <svg v-if="!value" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round">
          <path d="M3 8a5 5 0 1 0 10 0A5 5 0 0 0 3 8z"/>
          <path d="M4.5 4.5l7 7"/>
        </svg>
      </span>
      <span class="cp-trigger__text" :class="{ 'cp-trigger__text--empty': !value }">
        {{ value || 'Pick a color…' }}
      </span>
      <button v-if="value && !disabled" class="cp-clear" @click.stop="$emit('input', '')" tabindex="-1">
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M2 2l8 8M10 2l-8 8"/>
        </svg>
      </button>
    </div>

    <Teleport to="body">
      <div v-if="open" class="cp-panel" :style="panelStyle">
        <div class="cp-presets">
          <button
            v-for="color in presets"
            :key="color"
            class="cp-preset"
            :class="{ 'cp-preset--active': value === color }"
            :style="{ background: color }"
            :title="color"
            @click="select(color)"
          ></button>
        </div>
        <div class="cp-divider"></div>
        <div class="cp-custom">
          <div class="cp-custom__preview" :style="customColor ? { background: customColor } : {}"></div>
          <input
            class="cp-custom__input"
            type="text"
            v-model="customColor"
            placeholder="#hex or color name"
            @keydown.enter="applyCustom"
            spellcheck="false"
          />
          <button class="cp-custom__apply" @click="applyCustom">Apply</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
  export default {
    name: 'ColorPicker',
    props: ['value', 'label', 'disabled'],
    data: () => ({
      open: false,
      panelStyle: {},
      customColor: '',
      presets: [
        '#E57373', '#EF8C6F', '#F4A560', '#F6C344', '#A8C96B',
        '#66BB6A', '#4CAF84', '#4DB6AC', '#4BA3C7', '#4A7FA5',
        '#5472A3', '#7E70A8', '#A06BA3', '#C06B8C', '#8D8D8D',
        '#B07850', '#78909C', '#546E7A', '#3D6A90', '#2E7D52',
        '#C62828', '#6A1B9A', '#1565C0', '#00695C',
      ],
    }),
    mounted() {
      document.addEventListener('mousedown', this.onOutsideClick)
    },
    beforeUnmount() {
      document.removeEventListener('mousedown', this.onOutsideClick)
    },
    methods: {
      toggle() {
        if (this.disabled) return
        if (!this.open) {
          this.customColor = this.value || ''
          this.updatePanelPosition()
        }
        this.open = !this.open
      },
      select(color) {
        this.$emit('input', color)
        this.open = false
      },
      applyCustom() {
        if (this.customColor.trim()) {
          this.$emit('input', this.customColor.trim())
          this.open = false
        }
      },
      updatePanelPosition() {
        this.$nextTick(() => {
          const trigger = this.$refs.root?.querySelector('.cp-trigger')
          if (!trigger) return
          const rect = trigger.getBoundingClientRect()
          const spaceBelow = window.innerHeight - rect.bottom
          const panelHeight = 220
          const top = spaceBelow >= panelHeight || spaceBelow > rect.top
            ? rect.bottom + window.scrollY + 6
            : rect.top + window.scrollY - panelHeight - 6
          this.panelStyle = {
            position: 'absolute',
            top: top + 'px',
            left: rect.left + window.scrollX + 'px',
            width: '232px',
            zIndex: 10001,
          }
        })
      },
      onOutsideClick(e) {
        if (!this.open) return
        const panel = document.querySelector('.cp-panel')
        if (!this.$refs.root?.contains(e.target) && !panel?.contains(e.target)) {
          this.open = false
        }
      },
    }
  }
</script>

<style scoped>
.cp-root {
  position: relative;
  width: 100%;
}

.cp-label {
  display: block;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 4px;
}

.cp-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid var(--app-border);
  border-radius: var(--app-radius-md);
  background: var(--app-bg-input);
  cursor: pointer;
  min-height: 36px;
  transition: border-color var(--app-transition);
  user-select: none;
}

.cp-trigger:hover:not(.cp-trigger--disabled) {
  border-color: rgba(0, 0, 0, 0.18);
}

.cp-trigger--open {
  border-color: rgba(61, 106, 144, 0.5) !important;
}

.cp-trigger--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cp-swatch {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.3);
}

.cp-swatch svg { width: 11px; height: 11px; }

.cp-trigger__text {
  flex: 1;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.8);
  font-family: monospace;
}

.cp-trigger__text--empty {
  color: rgba(0, 0, 0, 0.38);
  font-family: inherit;
  font-size: 0.875rem;
}

.cp-clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  color: rgba(0, 0, 0, 0.3);
  transition: color 0.12s, background 0.12s;
  flex-shrink: 0;
}
.cp-clear svg { width: 9px; height: 9px; }
.cp-clear:hover { color: rgba(0, 0, 0, 0.7); background: rgba(0, 0, 0, 0.07); }
</style>

<style>
/* Global — panel is teleported outside component scope */
.cp-panel {
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 12px;
  box-sizing: border-box;
}

.cp-presets {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 6px;
}

.cp-preset {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 50%;
  border: 2px solid transparent;
  cursor: pointer;
  padding: 0;
  transition: transform 0.1s, border-color 0.1s;
}

.cp-preset:hover {
  transform: scale(1.15);
}

.cp-preset--active {
  border-color: rgba(0, 0, 0, 0.5);
  transform: scale(1.1);
}

.cp-divider {
  height: 1px;
  background: rgba(0, 0, 0, 0.07);
  margin: 10px 0;
}

.cp-custom {
  display: flex;
  align-items: center;
  gap: 6px;
}

.cp-custom__preview {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: rgba(0, 0, 0, 0.06);
}

.cp-custom__input {
  flex: 1;
  height: 28px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  background: rgba(0, 0, 0, 0.03);
  padding: 0 8px;
  font-size: 0.8rem;
  font-family: monospace;
  color: rgba(0, 0, 0, 0.75);
  outline: none;
  min-width: 0;
}

.cp-custom__input:focus {
  border-color: rgba(61, 106, 144, 0.5);
}

.cp-custom__apply {
  height: 28px;
  padding: 0 10px;
  border: none;
  border-radius: 6px;
  background: var(--primary);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.15s;
}

.cp-custom__apply:hover { opacity: 0.88; }
</style>
